import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationNS_Admin } from '@gokrew/models/constants';
export const DEAL_STAGES = [
  'qualified',
  'demo',
  'trial',
  'won',
  'lost',
] as const;
/**
 * Turns the composer's date field into the activity's dates: a task's date is
 * its due date; a note, call or meeting happened then (blank = now), except a
 * call or meeting in the future, which is planned and so gets a due date.
 */
export const activityDates = (
  kind: 'note' | 'call' | 'meeting' | 'task',
  local: string
): { occurredAt?: string; dueAt: string | null } => {
  // A task is due on a day, no time: the field holds "2026-11-05".
  if (kind === 'task') return { dueAt: local || null };
  const when = local ? new Date(local).toISOString() : null;
  if (when && kind !== 'note' && new Date(when).getTime() > Date.now())
    return { dueAt: when };
  return { dueAt: null, ...(when ? { occurredAt: when } : {}) };
};
export const arrDollars = (cents: number | null | undefined) =>
  Math.ceil((cents ?? 0) / 10000) * 100;
/** Now as a datetime-local value ("2026-10-30T21:55"), the composer default. */
export const nowLocal = () => {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
};
/** An ISO instant as a datetime-local value, for editing. */
export const toLocalInput = (iso: string) => {
  const when = new Date(iso);
  return new Date(when.getTime() - when.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
};
/** An ISO instant as a date input value ("2026-11-05"), for a task's day. */
export const toLocalDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-CA');
/** Today plus six months as a date input value, a new deal's close date. */
export const sixMonthsOut = () => {
  const now = new Date();
  return new Date(
    now.getFullYear(),
    now.getMonth() + 6,
    now.getDate()
  ).toLocaleDateString('en-CA');
};
/** Record changes (created, moved, edited): a deal's Journal, not its activity. */
export const CRM_LOG_KINDS = [
  'deal_created',
  'deal_updated',
  'stage_changed',
  'deal_stage_changed',
  'contact_created',
  'contact_updated',
  'task_completed',
  'activity_edited',
];
/** "JP" for JP Bray: first letters of first and last name, else the email. */
export const personInitials = (
  person?: {
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
  } | null
) =>
  (
    [person?.firstName, person?.lastName]
      .map((part) => part?.trim().charAt(0) ?? '')
      .join('') ||
    person?.email?.slice(0, 2) ||
    ''
  ).toLocaleUpperCase();
export const personName = (
  person?: {
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
  } | null
) =>
  [person?.firstName, person?.lastName].filter(Boolean).join(' ') ||
  person?.email ||
  '';
export const useCrmFormat = () => {
  const { t, i18n } = useTranslation(TranslationNS_Admin.CompanyInfo);
  const money = useCallback(
    (cents: number | null | undefined) =>
      cents == null
        ? t('crm.unknown')
        : new Intl.NumberFormat(i18n.language, {
            style: 'currency',
            currency: 'CAD',
            currencyDisplay: 'narrowSymbol',
            maximumFractionDigits: 0,
          }).format(cents / 100),
    [i18n.language, t]
  );
  const date = useCallback(
    (value: string | null | undefined, time = false) =>
      value
        ? new Intl.DateTimeFormat(i18n.language, {
            dateStyle: 'medium',
            ...(time ? { timeStyle: 'short' as const } : {}),
          }).format(new Date(value.length === 10 ? `${value}T12:00:00` : value))
        : t('crm.workspace.not_scheduled'),
    [i18n.language, t]
  );
  /** ARR for the board: whole dollars, rounded up to the next hundred. */
  const arr = useCallback(
    (cents: number | null | undefined) =>
      cents == null
        ? t('crm.unknown')
        : new Intl.NumberFormat(i18n.language, {
            style: 'currency',
            currency: 'CAD',
            currencyDisplay: 'narrowSymbol',
            maximumFractionDigits: 0,
          }).format(arrDollars(cents)),
    [i18n.language, t]
  );
  /** Compact date and time for activity rows; the year only when not this one. */
  const shortDateTime = useCallback(
    (value: string) => {
      const when = new Date(value);
      return new Intl.DateTimeFormat(i18n.language, {
        month: 'short',
        day: 'numeric',
        ...(when.getFullYear() === new Date().getFullYear()
          ? {}
          : { year: 'numeric' as const }),
        hour: 'numeric',
        minute: '2-digit',
      }).format(when);
    },
    [i18n.language]
  );
  return { money, arr, date, shortDateTime, t, i18n };
};
